'use client';

import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { useGuardedRouter } from '@/shared/components/navigation-guard';
import {
  Search, X, FileText, Users, Receipt, Settings, Loader2,
  LayoutDashboard, Package, CreditCard, Truck, Hash, Landmark,
  UserCheck, UserCog, ShieldCheck, Percent, Tags, FolderOpen, Coins,
  Warehouse, ArrowLeftRight, ClipboardList, ClipboardCheck, BarChart3,
  Calculator, Code, Banknote, RefreshCw, HelpCircle, TrendingUp,
  Wallet, PlusCircle, Building2, Car,
} from 'lucide-react';

import { NAV_GROUPS as SETARI_GROUPS } from '@/features/settings/components/settings-nav';
import type { Permission } from '@softfactura/shared';
import { usePermissions } from '@/features/auth/hooks/use-permissions';
import { useCompany } from '@/features/settings/hooks/use-settings';
import { canOpenPath } from '@/shared/components/route-permissions';
import { formatCurrency } from '@/shared/utils/number-format';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.softfactura.ro';

/* ─── Diacritic-insensitive match ─────────────────────────────────────
   `setari` matches `Setări`, `vanzari` matches `Vânzări`, `incasari` →
   `Încasări`, etc. Strip combining diacritics (Unicode NFD) BEFORE
   comparing — userii la viteză nu pun semne diacritice, iar Romanian
   are ă/â/î/ș/ț pe aproape orice meniu. */
function normalize(s: string): string {
  // ̀-ͯ = Unicode block "Combining Diacritical Marks" (after NFD
  // decomposition, ăâîșțĂÂÎȘȚ devin literă de bază + combining char).
  return (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}
function matches(haystack: string, needle: string): boolean {
  return normalize(haystack).includes(normalize(needle));
}

/* ─── Navigation items ───────────────────────────────────────────────── */
export const NAV_ITEMS = [
  { label: 'Dashboard', icon: LayoutDashboard, href: '/', kw: 'acasă home panou', group: 'Principal' },

  // Vânzări
  { label: 'Facturi', icon: FileText, href: '/facturi', kw: 'invoices vanzari emise', group: 'Vânzări' },
  { label: 'Factură nouă', icon: PlusCircle, href: '/facturi/nou', kw: 'new invoice creare adaugă', group: 'Vânzări' },
  { label: 'Proforme', icon: FileText, href: '/proforme', kw: 'proforma', group: 'Vânzări' },
  { label: 'Proformă nouă', icon: PlusCircle, href: '/proforme/nou', kw: 'proforma new creare', group: 'Vânzări' },
  { label: 'Avize', icon: FileText, href: '/avize', kw: 'aviz transport livrare', group: 'Vânzări' },
  { label: 'Aviz nou', icon: PlusCircle, href: '/avize/nou', kw: 'aviz new creare', group: 'Vânzări' },
  { label: 'Oferte', icon: FileText, href: '/oferte', kw: 'oferte cotatii devize', group: 'Vânzări' },
  { label: 'Ofertă nouă', icon: PlusCircle, href: '/oferte/nou', kw: 'oferta new creare', group: 'Vânzări' },
  { label: 'Facturi recurente', icon: RefreshCw, href: '/facturi-recurente', kw: 'recurente periodice abonament', group: 'Vânzări' },
  { label: 'Încasări', icon: Banknote, href: '/incasari', kw: 'payments plati bani', group: 'Vânzări' },
  { label: 'Chitanțe', icon: Receipt, href: '/chitante', kw: 'chitante numerar', group: 'Vânzări' },

  // Achiziții
  { label: 'Cheltuieli', icon: Wallet, href: '/cheltuieli', kw: 'expenses furnizori achizitii', group: 'Achiziții' },
  { label: 'Cheltuială nouă', icon: PlusCircle, href: '/cheltuieli/nou', kw: 'expense new adauga', group: 'Achiziții' },
  { label: 'Facturi primite', icon: Receipt, href: '/efactura', kw: 'efactura preluate primite anaf inbox spv furnizor', group: 'Achiziții' },
  { label: 'Comenzi furnizori', icon: Receipt, href: '/achizitii', kw: 'comenzi achizitie furnizori po purchase orders', group: 'Achiziții' },
  { label: 'Recepții (NIR)', icon: ClipboardCheck, href: '/receptie', kw: 'nir receptie marfa intrare', group: 'Achiziții' },
  { label: 'Plăți furnizori', icon: CreditCard, href: '/plati-furnizori', kw: 'sold furnizori datorii', group: 'Achiziții' },
  // Furnizori e listat în grupul „Nomenclatoare" mai jos (catalog entities)
  // Bancă + Trezorerie promovate top-level (industry standard); rămân ca grup separat în palette pentru clarity
  { label: 'Bancă', icon: CreditCard, href: '/banca', kw: 'banca extras tranzactii reconciliere', group: 'Bancă & Trezorerie' },
  { label: 'Trezorerie', icon: Landmark, href: '/trezorerie', kw: 'trezorerie cash flow lichiditate', group: 'Bancă & Trezorerie' },
  { label: 'Ride-sharing', icon: Car, href: '/transport-alternativ', kw: 'ride sharing hailing uber bolt transport alternativ curse', group: 'Activitate' },

  // Rapoarte / Contabilitate
  { label: 'Rapoarte', icon: BarChart3, href: '/rapoarte', kw: 'reports analize statistici', group: 'Rapoarte' },
  { label: 'Contabilitate', icon: Calculator, href: '/contabilitate', kw: 'contabilitate jurnal balanta', group: 'Rapoarte' },

  // Stocuri
  { label: 'Stoc curent', icon: Package, href: '/stocuri', kw: 'stoc curent', group: 'Stocuri' },
  { label: 'Mișcări stoc', icon: ArrowLeftRight, href: '/stocuri/rapoarte/miscari', kw: 'miscari stoc tranzactii', group: 'Stocuri' },
  { label: 'Vechime stoc', icon: TrendingUp, href: '/stocuri/vechime-stoc', kw: 'vechime aging fefo', group: 'Stocuri' },
  { label: 'Rotație stoc', icon: RefreshCw, href: '/stocuri/rotatie-stoc', kw: 'rotatie turnover viteza', group: 'Stocuri' },
  { label: 'Rapoarte stoc', icon: BarChart3, href: '/stocuri/rapoarte', kw: 'rapoarte stoc valoare', group: 'Stocuri' },
  { label: 'Operații stoc', icon: ClipboardList, href: '/stocuri/operatii', kw: 'operatii', group: 'Stocuri' },
  { label: 'Operații → Ieșire (consum)', icon: ClipboardList, href: '/stocuri/operatii/iesire', kw: 'iesire bon consum stoc', group: 'Stocuri' },
  { label: 'Operații → Transfer', icon: ClipboardList, href: '/stocuri/operatii/transfer', kw: 'transfer stoc depozit', group: 'Stocuri' },
  { label: 'Operații → Inventar', icon: ClipboardList, href: '/stocuri/operatii/inventariere', kw: 'inventar inventariere ajustare stoc fizic', group: 'Stocuri' },
  { label: 'Descărcare facturi (stoc)', icon: PlusCircle, href: '/stocuri/descarcare-facturi', kw: 'descarcare facturi stoc bulk', group: 'Stocuri' },
  { label: 'Depozite', icon: Warehouse, href: '/stocuri/depozite', kw: 'depozite magazie locatii', group: 'Stocuri' },
  { label: 'Scanner mobil', icon: Hash, href: '/m/scanner', kw: 'scanner barcode mobil pwa', group: 'Stocuri', mobileOnly: true },

  // Nomenclatoare
  { label: 'Clienți', icon: Users, href: '/clienti', kw: 'clients parteneri companii', group: 'Nomenclatoare' },
  { label: 'Furnizori', icon: Building2, href: '/furnizori', kw: 'suppliers vendors', group: 'Nomenclatoare' },
  { label: 'Produse & Servicii', icon: Package, href: '/produse', kw: 'products servicii catalog', group: 'Nomenclatoare' },
  { label: 'Serii documente', icon: Hash, href: '/serii', kw: 'serii prefix numerotare', group: 'Nomenclatoare' },
  { label: 'Conturi bancare', icon: Landmark, href: '/conturi', kw: 'banca iban cont', group: 'Nomenclatoare' },
  { label: 'Delegați', icon: UserCheck, href: '/delegati', kw: 'delegati transport sofer', group: 'Nomenclatoare' },
  { label: 'Emitenți', icon: UserCog, href: '/emitenti', kw: 'emitenti contacte', group: 'Nomenclatoare' },
  { label: 'Gestionari', icon: ShieldCheck, href: '/gestionari', kw: 'gestionari depozit', group: 'Nomenclatoare' },
  { label: 'Cote TVA', icon: Percent, href: '/cote-tva', kw: 'tva vat cota', group: 'Nomenclatoare' },
  { label: 'Categorii cheltuieli', icon: Tags, href: '/nomenclatoare/categorii-cheltuieli', kw: 'categorii cheltuieli', group: 'Nomenclatoare' },
  { label: 'Grupuri clienți', icon: FolderOpen, href: '/grupuri', kw: 'grupuri clienti', group: 'Nomenclatoare' },
  { label: 'Valute & Cursuri', icon: Coins, href: '/valute', kw: 'valute cursuri schimb bnr', group: 'Nomenclatoare' },

  // Administrare
  { label: 'Echipa', icon: Users, href: '/utilizatori', kw: 'echipa utilizatori useri team', group: 'Administrare' },
  { label: 'Notificări', icon: Receipt, href: '/notificari', kw: 'notificari alerte mesaje', group: 'Administrare' },
  { label: 'Setări', icon: Settings, href: '/setari', kw: 'settings configurare profil', group: 'Administrare' },
  { label: 'Migrare date', icon: HelpCircle, href: '/migrare', kw: 'migrare import smartbill facturis', group: 'Administrare' },
  { label: 'API & Webhooks', icon: Code, href: '/dezvoltatori', kw: 'api webhooks developers', group: 'Administrare' },
  { label: 'Ajutor', icon: HelpCircle, href: '/ajutor', kw: 'help ajutor documentatie ghid', group: 'Administrare' },

  // Setări — toate sub-tab-urile, derivate din settings-nav (sursă unică,
  // zero drift la adăugarea de tab-uri noi).
  ...SETARI_GROUPS.flatMap((g) =>
    g.items.map((it) => ({
      label: it.label,
      icon: Settings,
      href: `/setari?tab=${it.id}`,
      kw: `setari ${g.label} ${it.label} ${it.id}`,
      group: `Setări · ${g.label}`,
      // aceleași condiții ca în meniul Setărilor (altfel tab-ul se deschide pe panoul „Fără acces”)
      permission: it.permission,
      adminOnly: it.adminOnly,
      legalForms: it.legalForms,
    })),
  ),
];

/* ─── Helpers pentru rezultatele business ─── */
const TYPE_META: Record<string, { label: string; path: string; cls: string }> = {
  factura:  { label: 'Factură',  path: '/facturi',  cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  proforma: { label: 'Proformă', path: '/proforme', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  aviz:     { label: 'Aviz',     path: '/avize',    cls: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300' },
  oferta:   { label: 'Ofertă',   path: '/oferte',   cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
  storno:   { label: 'Storno',   path: '/facturi',  cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
};

const STATUS_RO: Record<string, string> = {
  draft: 'Ciornă', issued: 'Emisă', paid: 'Plătită',
  partially_paid: 'Parțial', overdue: 'Restantă', cancelled: 'Anulată', storno: 'Storno',
};

function fmtDate(d?: string | Date): string {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return ''; }
}

function fmtAmount(a: number, c = 'RON'): string {
  return formatCurrency(a, c);
}

function fmtNumber(n: number): string {
  return n > 0 ? String(n).padStart(5, '0') : '-';
}

/* ─── Types ─── */
interface BusinessResults {
  invoices: any[];
  receipts: any[];
  clients: any[];
}

/* ─── Document result row ─── */
function DocRow({
  inv, idx, sel, onGo,
}: {
  inv: any; idx: number; sel: number; onGo: (href: string) => void;
}) {
  const meta = TYPE_META[inv.type as string] || TYPE_META.factura!;
  const href = `${meta.path}?view=${inv.id}`;
  const numDisplay = (inv.series ? `${inv.series} ` : '') + fmtNumber(Number(inv.number));
  const statusLabel = STATUS_RO[inv.status as string] || inv.status || '';
  return (
    <button
      data-idx={idx}
      onClick={() => onGo(href)}
      className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
        idx === sel
          ? 'bg-primary/10 text-primary dark:bg-primary/20'
          : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
      }`}
    >
      <span className={`shrink-0 rounded px-1.5 py-0.5 text-[0.625rem] font-semibold ${meta.cls}`}>
        {meta.label}
      </span>
      <span className="font-mono text-xs tabular-nums w-24 shrink-0 text-left">{numDisplay}</span>
      <span className="flex-1 text-left truncate">
        {inv.client?.name || <span className="text-neutral-400">—</span>}
      </span>
      <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums shrink-0">
        {fmtDate(inv.issueDate)}
      </span>
      <span className="text-xs font-medium tabular-nums shrink-0 w-24 text-right">
        {fmtAmount(Number(inv.total || 0), inv.currency || 'RON')}
      </span>
      {statusLabel && (
        <span className="text-[0.625rem] text-neutral-400 shrink-0 w-16 text-right">
          {statusLabel}
        </span>
      )}
    </button>
  );
}

/**
 * Un rând din paletă se vede: adresa lui se poate deschide (ROUTE_PERMISSIONS), iar la tab-urile din Setări și dreptul,
 * „doar admin” și forma juridică, exact ca în `SettingsNav` (altfel tab-ul se deschidea pe panoul „Fără acces”).
 */
export function vizibilInPaleta(
  it: { href: string; mobileOnly?: boolean; permission?: Permission | Permission[]; adminOnly?: boolean; legalForms?: readonly string[] },
  c: { can: (p: Permission) => boolean; canAny: (p: Permission[]) => boolean; role?: string; legalForm?: string; isMobileOrTablet: boolean },
): boolean {
  if (it.mobileOnly && !c.isMobileOrTablet) return false;
  if (!canOpenPath(it.href, c.can)) return false;
  if (it.permission && !(Array.isArray(it.permission) ? c.canAny(it.permission) : c.can(it.permission))) return false;
  if (it.adminOnly && c.role !== 'owner' && c.role !== 'admin') return false;
  if (it.legalForms && (!c.legalForm || !it.legalForms.includes(c.legalForm))) return false;
  return true;
}

export function CommandPalette() {
  const isMobileOrTablet = useIsMobile(1024);
  // Paginile restricționate (ex. creările, pentru un cont doar de citire) apar doar cu dreptul lor.
  const { can, canAny, role } = usePermissions();
  const legalForm = useCompany().data?.legalForm ?? undefined;
  const visibleItems = useMemo(
    () => NAV_ITEMS.filter((it) => vizibilInPaleta(it, { can, canAny, role, legalForm, isMobileOrTablet })),
    [isMobileOrTablet, can, canAny, role, legalForm],
  );

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [sel, setSel] = useState(0);
  const [results, setResults] = useState<BusinessResults>({ invoices: [], receipts: [], clients: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const router = useGuardedRouter();

  /* Open/close — Cmd+K toggles, Esc closes (handled in onKeyDown). */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setOpen(p => !p); }
    };
    const onOpen = () => setOpen(true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('open-command-palette', onOpen);
    return () => { document.removeEventListener('keydown', onKey); window.removeEventListener('open-command-palette', onOpen); };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery(''); setSel(0); setResults({ invoices: [], receipts: [], clients: [] });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  /* Debounced business search — fires after 300ms quiet typing.
     Anulează request stale via AbortController la fiecare query nou. */
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults({ invoices: [], receipts: [], clients: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/v1/search?q=${encodeURIComponent(q)}`, {
          credentials: 'include',
          signal: ctrl.signal,
        });
        if (!res.ok) {
          setResults({ invoices: [], receipts: [], clients: [] });
          return;
        }
        const data = await res.json();
        setResults({
          invoices: data.invoices || [],
          receipts: data.receipts || [],
          clients: data.clients || [],
        });
      } catch (e: any) {
        if (e?.name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.error('Search error:', e);
        }
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [query]);

  /* Build flat items: nav matches → invoices → receipts → clients.
     Folosit doar pentru navigare keyboard cu săgeți / Enter. */
  const flatItems = useCallback(() => {
    const q = query.trim();
    if (q.length < 2) {
      // Empty / scurt query → arătăm toate meniurile grupate (modul vechi).
      return visibleItems.map(n => ({ kind: 'nav' as const, href: n.href, data: n }));
    }
    const navMatches = visibleItems.filter(n => matches(n.label, q) || matches(n.kw, q));
    const items: Array<{ kind: 'nav'; href: string; data: any }
                    | { kind: 'doc'; href: string; data: any }
                    | { kind: 'receipt'; href: string; data: any }
                    | { kind: 'client'; href: string; data: any }> = [];
    for (const n of navMatches) items.push({ kind: 'nav', href: n.href, data: n });
    for (const inv of results.invoices) {
      const meta = TYPE_META[inv.type] || TYPE_META.factura!;
      items.push({ kind: 'doc', href: `${meta.path}?view=${inv.id}`, data: inv });
    }
    for (const r of results.receipts) {
      items.push({ kind: 'receipt', href: `/incasari?view=${r.id}`, data: r });
    }
    for (const c of results.clients) {
      items.push({ kind: 'client', href: `/clienti?search=${encodeURIComponent(c.name || '')}`, data: c });
    }
    return items;
  }, [query, results, visibleItems]);

  const items = flatItems();

  /* Reset selection when results change. */
  useEffect(() => { setSel(0); }, [query, results]);

  /* Scroll selected into view. */
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${sel}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  /* Group nav items by `group` key — preserves declaration order via Map.
     Folosit doar pentru render-ul din empty state. */
  const groupedNav = (() => {
    const map = new Map<string, typeof visibleItems>();
    for (const it of visibleItems) {
      const g = it.group || 'Altele';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(it);
    }
    return Array.from(map.entries());
  })();

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(p => Math.min(p + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(p => Math.max(p - 1, 0)); }
    else if (e.key === 'Enter' && items[sel]?.href) { e.preventDefault(); go(items[sel]!.href); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  const go = (href: string) => { setOpen(false); router.push(href); };

  if (!open) return null;

  const q = query.trim();
  const hasQuery = q.length >= 2;
  const navMatches = hasQuery ? visibleItems.filter(n => matches(n.label, q) || matches(n.kw, q)) : [];
  const facturi = results.invoices.filter((i: any) => i.type === 'factura' || i.type === 'storno');
  const proforme = results.invoices.filter((i: any) => i.type === 'proforma');
  const avize = results.invoices.filter((i: any) => i.type === 'aviz');
  const oferte = results.invoices.filter((i: any) => i.type === 'oferta');
  const totalResults = navMatches.length + results.invoices.length + results.receipts.length + results.clients.length;

  let flatIdx = 0;

  return (
    <div className="fixed inset-0 z-[100]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />

      {/* Dialog — wrapper-ul de pozitionare acopera toata latimea (inset-x-0),
          deci click-urile din stanga/dreapta panoului aterizau pe el, NU pe
          backdrop => modalul nu se inchidea la click in afara. Inchidem si de
          aici; panoul opreste propagarea. */}
      <div className="absolute inset-x-0 top-[15%] flex justify-center px-4" onClick={() => setOpen(false)}>
        <div className="w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-2xl overflow-hidden" onKeyDown={onKeyDown}>
          {/* Input */}
          <div className="flex items-center border-b border-neutral-200 dark:border-neutral-700 px-4">
            <Search className="h-4 w-4 text-neutral-400 shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Caută factură, client, sumă, dată..."
              className="w-full bg-transparent py-3.5 px-3 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 outline-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            {loading && <Loader2 className="h-4 w-4 text-neutral-400 animate-spin shrink-0 mr-2" />}
            <button onClick={() => setOpen(false)} className="shrink-0 rounded-md p-1 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[60vh] overflow-y-auto">
            {/* Empty / scurt query → 2 coloane (Navigare + Ce poți căuta?) */}
            {!hasQuery && (
              <div className="flex">
                <div className="w-[55%] border-r border-neutral-100 dark:border-neutral-800 p-2">
                  {groupedNav.map(([groupName, groupItems]) => (
                    <div key={groupName} className="mb-2 last:mb-0">
                      <p className="px-3 py-1.5 text-[0.6875rem] font-medium text-neutral-400 uppercase tracking-wider">
                        {groupName}
                      </p>
                      {groupItems.map((n) => {
                        const idx = flatIdx++;
                        return (
                          <button
                            key={n.href + ':' + n.label}
                            data-idx={idx}
                            onClick={() => go(n.href)}
                            className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                              idx === sel
                                ? 'bg-primary/10 text-primary dark:bg-primary/20'
                                : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                            }`}
                          >
                            <n.icon className="h-4 w-4 shrink-0" />
                            <span className="flex-1 text-left">{n.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div className="flex-1 p-3">
                  <p className="px-2 py-1.5 text-[0.6875rem] font-semibold text-neutral-900 dark:text-neutral-100 uppercase tracking-wider">
                    Ce poți căuta?
                  </p>
                  <div className="space-y-2.5 px-2 pt-1">
                    {[
                      { label: 'Nr. document', example: '00042 sau BWLS-42964', color: 'text-blue-500' },
                      { label: 'Client', example: 'nume, CUI, email, telefon', color: 'text-violet-500' },
                      { label: 'Produs', example: 'cod sau denumire', color: 'text-teal-500' },
                      { label: 'Sumă', example: '91.52', color: 'text-amber-500' },
                      { label: 'Data emiterii', example: '15.04.2026', color: 'text-orange-500' },
                      { label: 'Chitanță', example: '0001', color: 'text-emerald-500' },
                      { label: 'Observații', example: 'text din note', color: 'text-neutral-400' },
                    ].map(item => (
                      <div key={item.label} className="flex items-baseline gap-3">
                        <span className="text-sm font-semibold w-24 shrink-0 text-neutral-900 dark:text-neutral-100">
                          {item.label}
                        </span>
                        <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
                          {item.example}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Loading state — query în curs, niciun rezultat încă */}
            {hasQuery && loading && totalResults === 0 && (
              <div className="flex items-center justify-center py-10 text-sm text-neutral-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Se caută...
              </div>
            )}

            {/* No results */}
            {hasQuery && !loading && totalResults === 0 && (
              <div className="py-10 text-center text-sm text-neutral-500 dark:text-neutral-400">
                Niciun rezultat pentru <strong>&quot;{query}&quot;</strong>
              </div>
            )}

            {/* Cu rezultate */}
            {hasQuery && totalResults > 0 && (
              <div className="p-2 space-y-1">
                {/* ── Navigare ── */}
                {navMatches.length > 0 && (
                  <>
                    <p className="px-3 py-1.5 text-[0.6875rem] font-medium text-neutral-400 uppercase tracking-wider flex items-center gap-1.5">
                      <Search className="h-3 w-3" /> Navigare
                    </p>
                    {navMatches.map(n => {
                      const idx = flatIdx++;
                      return (
                        <button
                          key={n.href + ':' + n.label}
                          data-idx={idx}
                          onClick={() => go(n.href)}
                          className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors ${
                            idx === sel
                              ? 'bg-primary/10 text-primary dark:bg-primary/20'
                              : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                          }`}
                        >
                          <n.icon className="h-4 w-4 shrink-0" />
                          <span className="flex-1 text-left">{n.label}</span>
                          {n.group && <span className="text-[0.625rem] text-neutral-400">{n.group}</span>}
                        </button>
                      );
                    })}
                  </>
                )}

                {/* ── Facturi (inclusiv storno) ── */}
                {facturi.length > 0 && (
                  <>
                    <p className="px-3 py-1.5 mt-2 text-[0.6875rem] font-medium text-neutral-400 uppercase tracking-wider flex items-center gap-1.5">
                      <FileText className="h-3 w-3" /> Facturi
                    </p>
                    {facturi.map((inv: any) => {
                      const idx = flatIdx++;
                      return <DocRow key={inv.id} inv={inv} idx={idx} sel={sel} onGo={go} />;
                    })}
                  </>
                )}

                {/* ── Proforme ── */}
                {proforme.length > 0 && (
                  <>
                    <p className="px-3 py-1.5 mt-2 text-[0.6875rem] font-medium text-neutral-400 uppercase tracking-wider flex items-center gap-1.5">
                      <FileText className="h-3 w-3" /> Proforme
                    </p>
                    {proforme.map((inv: any) => {
                      const idx = flatIdx++;
                      return <DocRow key={inv.id} inv={inv} idx={idx} sel={sel} onGo={go} />;
                    })}
                  </>
                )}

                {/* ── Avize ── */}
                {avize.length > 0 && (
                  <>
                    <p className="px-3 py-1.5 mt-2 text-[0.6875rem] font-medium text-neutral-400 uppercase tracking-wider flex items-center gap-1.5">
                      <FileText className="h-3 w-3" /> Avize
                    </p>
                    {avize.map((inv: any) => {
                      const idx = flatIdx++;
                      return <DocRow key={inv.id} inv={inv} idx={idx} sel={sel} onGo={go} />;
                    })}
                  </>
                )}

                {/* ── Oferte ── */}
                {oferte.length > 0 && (
                  <>
                    <p className="px-3 py-1.5 mt-2 text-[0.6875rem] font-medium text-neutral-400 uppercase tracking-wider flex items-center gap-1.5">
                      <FileText className="h-3 w-3" /> Oferte
                    </p>
                    {oferte.map((inv: any) => {
                      const idx = flatIdx++;
                      return <DocRow key={inv.id} inv={inv} idx={idx} sel={sel} onGo={go} />;
                    })}
                  </>
                )}

                {/* ── Chitanțe ── */}
                {results.receipts.length > 0 && (
                  <>
                    <p className="px-3 py-1.5 mt-2 text-[0.6875rem] font-medium text-neutral-400 uppercase tracking-wider flex items-center gap-1.5">
                      <Receipt className="h-3 w-3" /> Chitanțe
                    </p>
                    {results.receipts.map((r: any) => {
                      const idx = flatIdx++;
                      const href = `/incasari?view=${r.id}`;
                      const numDisplay = (r.series ? `${r.series} ` : '') + fmtNumber(Number(r.number));
                      return (
                        <button
                          key={r.id}
                          data-idx={idx}
                          onClick={() => go(href)}
                          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                            idx === sel
                              ? 'bg-primary/10 text-primary dark:bg-primary/20'
                              : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                          }`}
                        >
                          <span className="shrink-0 rounded px-1.5 py-0.5 text-[0.625rem] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                            Chitanță
                          </span>
                          <span className="font-mono text-xs tabular-nums w-24 shrink-0 text-left">{numDisplay}</span>
                          <span className="flex-1 text-left truncate">{r.clientName || <span className="text-neutral-400">—</span>}</span>
                          <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums shrink-0">{fmtDate(r.issueDate)}</span>
                          <span className="text-xs font-medium tabular-nums shrink-0 w-24 text-right">{fmtAmount(Number(r.amount || 0), r.currency || 'RON')}</span>
                        </button>
                      );
                    })}
                  </>
                )}

                {/* ── Clienți ── */}
                {results.clients.length > 0 && (
                  <>
                    <p className="px-3 py-1.5 mt-2 text-[0.6875rem] font-medium text-neutral-400 uppercase tracking-wider flex items-center gap-1.5">
                      <Users className="h-3 w-3" /> Clienți
                    </p>
                    {results.clients.map((c: any) => {
                      const idx = flatIdx++;
                      const href = `/clienti?search=${encodeURIComponent(c.name || '')}`;
                      return (
                        <button
                          key={c.id}
                          data-idx={idx}
                          onClick={() => go(href)}
                          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                            idx === sel
                              ? 'bg-primary/10 text-primary dark:bg-primary/20'
                              : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                          }`}
                        >
                          <Users className="h-4 w-4 shrink-0 text-violet-500" />
                          <span className="flex-1 text-left truncate">{c.name}</span>
                          <span className="text-xs text-neutral-500 dark:text-neutral-400 shrink-0 tabular-nums">{c.cui || ''}</span>
                          <span className="text-xs text-neutral-400 shrink-0 truncate max-w-[180px]">{c.email || c.phone || ''}</span>
                        </button>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between border-t border-neutral-200 dark:border-neutral-700 px-4 py-2">
            <div className="flex items-center gap-3 text-xs text-neutral-400">
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-neutral-200 dark:border-neutral-600 px-1.5 py-0.5 text-[0.625rem]">↑↓</kbd>
                navigare
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-neutral-200 dark:border-neutral-600 px-1.5 py-0.5 text-[0.625rem]">↵</kbd>
                deschide
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-neutral-200 dark:border-neutral-600 px-1.5 py-0.5 text-[0.625rem]">Esc</kbd>
                închide
              </span>
            </div>
            {hasQuery && totalResults > 0 && (
              <span className="text-xs text-neutral-400">{totalResults} rezultate</span>
            )}
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
